import { Mic, Square, Play, Pause, LogOut, RefreshCw } from "lucide-react";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { ConnectionState } from "../../types";

import { MAX_QUESTIONS } from "../../services/geminiLiveService";

interface LiveControlsProps {
  liveStatus: ConnectionState;
  questionCount: number;
  maxQuestions?: number;
  questionPhase: string;
  isLivePaused: boolean;
  startQuestion: () => void;
  endQuestion: () => void;
  toggleLivePause: () => void;
  onRetryConnection: () => void;
  handleEndConsultation: () => void;
}

const LiveControls: React.FC<LiveControlsProps> = ({
  liveStatus,
  questionCount,
  maxQuestions = MAX_QUESTIONS,
  questionPhase,
  isLivePaused,
  startQuestion,
  endQuestion,
  toggleLivePause,
  handleEndConsultation,
  onRetryConnection,
}) => {
  const { t } = useTranslation();
  const [retryCooldown, setRetryCooldown] = useState(5); // 첫 진입 시 5초 대기

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (retryCooldown > 0) {
      timer = setTimeout(() => {
        setRetryCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [retryCooldown]);

  const handleRetryClick = () => {
    if (retryCooldown > 0) return;
    onRetryConnection();
    setRetryCooldown(10); // 10초 쿨타임
  };

  return (
    <div className="btn-group reading-mode">
      {/* Left Section: Question Counter */}
      <div className="reading-controls-group navigation">
        {liveStatus === "connected" && (
          <div className="question-counter live-counter">
            {questionCount >= maxQuestions ? (
              <span className="limit-reached">{t("limitReached")}</span>
            ) : (
              <span>{t("questionCount", { count: questionCount, max: maxQuestions })}</span>
            )}
          </div>
        )}
      </div>

      {/* Middle Section: Main Action */}
      <div className="reading-controls-group progress live-progress">
        {liveStatus === "connected" && questionCount < maxQuestions && (
          <div className="btn-actions live-actions">
            {questionPhase === "idle" && (
              <button
                className="btn-toggle live-toggle resuming"
                onClick={startQuestion}
              >
                <Mic size={20} />
                {t("consultQuestion")}
              </button>
            )}

            {questionPhase === "listening" && (
              <button
                className="btn-toggle live-toggle is-listening"
                onClick={endQuestion}
              >
                <Square size={18} fill="currentColor" />
                {t("consultEndQuestion")}
              </button>
            )}

            {(questionPhase === "answering" || isLivePaused) && (
              <button
                className={`btn-toggle live-toggle ${isLivePaused ? "resuming" : ""}`}
                onClick={toggleLivePause}
              >
                {isLivePaused ? (
                  <>
                    <Play size={20} fill="currentColor" />
                    {t("resumeAnswering")}
                  </>
                ) : (
                  <>
                    <Pause size={20} fill="currentColor" />
                    {t("pauseAnswering")}
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Right Section: Status & End */}
      <div className="reading-controls-group settings" style={{ gap: "20px" }}>
        <div
          className={`status-indicator live-status ${questionPhase === "answering" ? "is-answering" : ""}`}
        >
          <span
            className={`status-dot ${liveStatus === "connected" ? (isLivePaused ? "paused" : "active") : "error"}`}
          />
          <span className="status-text live-text">
            {liveStatus === "connected"
              ? isLivePaused
                ? t("statusPaused")
                : questionPhase === "listening"
                  ? t("statusListening")
                  : questionPhase === "answering"
                    ? t("statusAnswering")
                    : t("statusWaiting")
              : liveStatus === ConnectionState.ERROR
                ? t("connectionError")
                : liveStatus === ConnectionState.RECONNECTING
                  ? t("reconnecting")
                  : t("connecting")}
          </span>
        </div>

        {liveStatus !== "connected" && questionCount < maxQuestions && (
          <button
            className="btn-retry"
            onClick={handleRetryClick}
            disabled={retryCooldown > 0}
          >
            <RefreshCw
              size={16}
              className={retryCooldown > 0 ? "spinning" : ""}
            />
            {retryCooldown > 0 ? `${retryCooldown}s` : t("retryConnection")}
          </button>
        )}

        {questionCount >= maxQuestions && (
          <button className="btn-end live-end" onClick={handleEndConsultation}>
            <LogOut size={16} />
            {t("endConsultation")}
          </button>
        )}
      </div>
    </div>
  );
};

export default LiveControls;
